import type { MembershipProject } from "@zworks/console-contracts";
import { CircleHelp, LogOut, MoreHorizontal, Settings } from "lucide-react";
import { ThemeToggle } from "@/components/theme-toggle";
import { signOut } from "@/lib/auth";
import { toast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const roleLabels: Record<MembershipProject["role"], string> = {
  owner: "Owner",
  developer: "Developer",
  approver: "Approver",
  viewer: "Viewer",
};

/**
 * The account menu: initials, name, role and project, settings (Cmd+,),
 * theme, help and docs, and sign out.
 */
export function ProfileMenu({
  initials,
  displayName,
  role,
  projectName,
  collapsed,
  onSignOut = signOut,
  onOpenSettings,
}: {
  initials: string;
  displayName: string;
  role: MembershipProject["role"] | null;
  projectName: string | null;
  collapsed: boolean;
  onSignOut?: () => Promise<void> | void;
  onOpenSettings?: () => void;
}) {
  const roleAndProject = role
    ? projectName
      ? `${roleLabels[role]} · ${projectName}`
      : roleLabels[role]
    : (projectName ?? "No project");

  return (
    <DropdownMenu>
      <div className="relative">
        <DropdownMenuTrigger
          aria-label={`Account menu, ${displayName}`}
          className={cn(
            "flex h-[2.125rem] w-full items-center gap-2 rounded-lg px-2 text-sm hover:bg-sidebar-accent transition-colors",
            collapsed && "justify-center px-0",
          )}
        >
          <span
            aria-hidden
            className="grid size-6 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold text-foreground shadow-2xs"
          >
            {initials}
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1 text-left leading-tight">
                <span className="block truncate font-medium">{displayName}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {roleAndProject}
                </span>
              </span>
              <MoreHorizontal className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            </>
          )}
        </DropdownMenuTrigger>

        <DropdownMenuContent
          aria-label="Account"
          side={collapsed ? "right" : "top"}
          className="w-64"
        >
          <DropdownMenuLabel className="flex items-center gap-3 px-2 py-2 normal-case">
            <span
              aria-hidden
              className="grid size-8 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold text-foreground"
            >
              {initials}
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-sm font-medium">{displayName}</span>
              <span className="block truncate text-xs text-muted-foreground">{roleAndProject}</span>
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              onOpenSettings?.();
            }}
          >
            <Settings className="size-4" aria-hidden />
            <span className="flex-1">Settings</span>
            <kbd className="pointer-events-none font-mono text-xs text-muted-foreground">⌘,</kbd>
          </DropdownMenuItem>
          <DropdownMenuLabel>Appearance</DropdownMenuLabel>
          <div className="px-2 pb-1.5">
            <ThemeToggle />
          </div>
          <DropdownMenuItem
            onClick={() => {
              toast("Documentation is not available yet.");
            }}
          >
            <CircleHelp className="size-4" aria-hidden />
            Help and docs
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              void onSignOut();
            }}
          >
            <LogOut className="size-4" aria-hidden />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </div>
    </DropdownMenu>
  );
}
